/**
 * FT-365 Option B — user-boundary sync-bridge state reset helper.
 *
 * The UniversalSyncBridge holds a set of user-scoped refs (syncQueue,
 * lastSyncTime, subscriptions, initialSyncComplete, reconciliationComplete,
 * justPulledCollections, pushCooldowns). Under v3.5.1 the Layer D
 * (VesselProviderKeyedByAuth) wrapper remounted the bridge on
 * sr:auth:userChanged, which reset all of these refs by re-initialising them.
 *
 * Under Option B, Layer D is bypassed and the app relies on
 * `window.location.reload()` from `dispatchUserBoundaryReload()` to give us
 * a truly fresh mount. But if that reload is EVER skipped or intercepted
 * (feature flag OFF, service worker intercept, browser bug, tab visibility
 * throttling delays the setTimeout), the bridge would live across the
 * boundary and leak state across users. This helper is the belt-and-braces:
 * on `sr:auth:userChanged` we synchronously reset the same subset of refs
 * that Layer D's remount used to reset.
 *
 * Extracting the reset into a pure helper (rather than inlining it in the
 * bridge's useEffect) means:
 *   - The invariant is testable without mounting the whole bridge with
 *     mocked VesselContext + azureCloudClient + storageRouter (etc.)
 *   - Reviewers can see the whole "what gets reset" surface in one place
 *   - A future refactor that changes the bridge's ref layout has ONE
 *     coupling point to update (this helper + its tests), not an inline
 *     block hiding inside a 1500-line component file.
 *
 * See feedback_auth_boundary_reload_invariant.md.
 */

/**
 * Reset the user-scoped subset of UniversalSyncBridge state refs.
 *
 * Each argument is expected to be a React ref object (`{ current }`).
 * We mutate `.current` in place so the same ref keeps its identity across
 * React renders (avoids gratuitous re-render cascades).
 *
 * Contract:
 *   - `syncQueue.current`            → empty array (`[]`)
 *   - `lastSyncTime.current`         → empty object (`{}`)
 *   - `subscriptions.current`        → empty object (`{}`)
 *   - `initialSyncComplete.current`  → `false`
 *   - `reconciliationComplete.current` → `false`
 *   - `justPulledCollections.current`→ **new empty Map** (not `.clear()`)
 *   - `pushCooldowns.current`        → **new empty Map** (not `.clear()`)
 *
 * The Maps are replaced with NEW instances rather than cleared in place —
 * this matches what Layer D's remount produced (fresh `useRef(new Map())`)
 * and is symmetric with the initial-mount surface, so any code that
 * captured a reference to the old Map instance is guaranteed to see an
 * empty replacement rather than a mutated one.
 *
 * Any missing ref (null/undefined) is silently skipped so a partial-mount
 * teardown never throws — the whole point is a safety net.
 *
 * FT-365 Option B HARDENING (2026-07-15): resetting the value-refs above is
 * necessary but NOT sufficient — it leaves LIVE resources from the previous
 * user running when the page does NOT reload (flag OFF / reload skipped or
 * intercepted). Two live leaks are closed here so the reset is correct by
 * construction, not dependent on remount/reload timing:
 *
 *   1. `subscriptions.current` held the ACTIVE unsubscribe fns for each
 *      collection's realtime listener. Nulling the object dropped our HANDLE
 *      to them but left the listeners running — a listener opened as user A
 *      could still fire a callback that writes user A's cloud data into user
 *      B's storage. We now CALL each unsubscribe fn (mirroring the normal
 *      unmount cleanup at UniversalSyncBridge.js:~1517) BEFORE dropping the
 *      handle, each wrapped in try/catch so one throw doesn't abort the rest.
 *
 *   2. In-flight push/pull fetches started as user A were untouched — a
 *      response landing after the boundary would resolve into user B's
 *      storage. The caller passes `abortInflight` (the module-level
 *      `abortAllInflightSync` from UniversalSyncBridge) so the logic lives
 *      in this tested helper; we invoke it if provided.
 *
 *   3. `isProcessing.current` (the processSyncQueue re-entrancy guard) is
 *      reset to false so a boundary that lands mid-drain can't wedge user
 *      B's queue with a stale "already processing" flag. The early-return
 *      guard at UniversalSyncBridge.js:~553 reads this same ref.
 *
 * @param {object} refs
 * @param {{current: any[]}=} refs.syncQueue
 * @param {{current: Record<string, any>}=} refs.lastSyncTime
 * @param {{current: Record<string, any>}=} refs.subscriptions
 * @param {{current: boolean}=} refs.initialSyncComplete
 * @param {{current: boolean}=} refs.reconciliationComplete
 * @param {{current: Map<any, any>}=} refs.justPulledCollections
 * @param {{current: Map<any, any>}=} refs.pushCooldowns
 * @param {{current: boolean}=} refs.isProcessing  processSyncQueue re-entrancy guard
 * @param {() => void =} refs.abortInflight  aborts module-level in-flight sync
 *   (pass `abortAllInflightSync` from UniversalSyncBridge). Kept as a param
 *   rather than imported so the abort is exercised by the helper's own tests.
 */
export function resetSyncBridgeUserScopedState(refs) {
  if (!refs) return;
  const {
    syncQueue,
    lastSyncTime,
    subscriptions,
    initialSyncComplete,
    reconciliationComplete,
    justPulledCollections,
    pushCooldowns,
    isProcessing,
    abortInflight,
  } = refs;

  // 1) Tear down LIVE realtime subscriptions before dropping our handle to
  //    them. Mirrors the normal unmount cleanup shape at
  //    UniversalSyncBridge.js:~1517 (typeof-guard + call), but adds a
  //    per-unsub try/catch so one throwing unsubscribe can't abort the rest
  //    of the teardown — the whole point is a safety net that must never
  //    leave a straggler listener alive.
  if (subscriptions && subscriptions.current) {
    Object.values(subscriptions.current).forEach((unsubscribe) => {
      if (typeof unsubscribe === 'function') {
        try { unsubscribe(); } catch (_) { /* non-fatal — keep tearing down */ }
      }
    });
  }

  // 2) Abort any in-flight push/pull so a fetch started as the previous user
  //    can't resolve into the next user's storage.
  if (typeof abortInflight === 'function') {
    try { abortInflight(); } catch (_) { /* non-fatal */ }
  }

  if (syncQueue) syncQueue.current = [];
  if (lastSyncTime) lastSyncTime.current = {};
  if (subscriptions) subscriptions.current = {};
  if (initialSyncComplete) initialSyncComplete.current = false;
  if (reconciliationComplete) reconciliationComplete.current = false;
  if (justPulledCollections) justPulledCollections.current = new Map();
  if (pushCooldowns) pushCooldowns.current = new Map();

  // 3) Clear the re-entrancy guard so the new user's queue can drain.
  if (isProcessing) isProcessing.current = false;
}
